import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_BUILD_HISTORY } from '../graphql/queries';
import { DataTable } from '../components/DataTable';
import OsIcon, { PackerBuildIcon } from '../components/OsIcon';

function phaseBadge(phase) {
  const map = { Succeeded: 'success', Running: 'info', Failed: 'error', Pending: 'pending', Error: 'error' };
  return <span className={`badge badge-${map[phase] || 'pending'}`}>{phase}</span>;
}

function formatDuration(startedAt, finishedAt) {
  if (!startedAt || !finishedAt) return '-';
  const ms = new Date(finishedAt) - new Date(startedAt);
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

const columns = [
  {
    accessorKey: 'name',
    header: 'Workflow',
    cell: ({ row }) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <PackerBuildIcon size={16} />
        <OsIcon name={row.original.name} size={16} />
        <Link to={`/build/${row.original.name}`} className="link">{row.original.name}</Link>
      </span>
    ),
  },
  {
    accessorKey: 'phase',
    header: 'Phase',
    cell: ({ getValue }) => phaseBadge(getValue()),
  },
  { accessorKey: 'progress', header: 'Progress', cell: ({ getValue }) => getValue() || '-' },
  {
    accessorKey: 'startedAt',
    header: 'Started',
    cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleString() : '-',
  },
  {
    accessorKey: 'finishedAt',
    header: 'Finished',
    cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleString() : '-',
  },
  {
    id: 'duration',
    header: 'Duration',
    cell: ({ row }) => formatDuration(row.original.startedAt, row.original.finishedAt),
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <Link to={`/build/${row.original.name}`} className="link" style={{ fontSize: 12 }}>
        View Logs
      </Link>
    ),
  },
];

export default function BuildHistory() {
  const { data, loading, error, fetchMore } = useQuery(GET_BUILD_HISTORY, { variables: { limit: 20 } });

  const builds = useMemo(() => data?.listBuildHistory?.items || [], [data]);
  const nextToken = data?.listBuildHistory?.nextToken;

  const summaryStats = useMemo(() => {
    if (builds.length === 0) return null;
    const succeeded = builds.filter(b => b.phase === 'Succeeded').length;
    const failed = builds.filter(b => b.phase === 'Failed' || b.phase === 'Error').length;
    const durations = builds
      .filter(b => b.startedAt && b.finishedAt)
      .map(b => new Date(b.finishedAt) - new Date(b.startedAt));
    const avgMs = durations.length > 0
      ? durations.reduce((a, b) => a + b, 0) / durations.length
      : 0;
    const mins = Math.floor(avgMs / 60000);
    const secs = Math.round((avgMs % 60000) / 1000);
    return {
      total: builds.length,
      succeeded,
      failed,
      successRate: Math.round((succeeded / builds.length) * 100),
      avgDuration: avgMs > 0 ? (mins > 0 ? `${mins}m ${secs}s` : `${secs}s`) : '-',
    };
  }, [builds]);

  if (loading) return <div className="loading">Loading build history...</div>;
  if (error) return <div className="error">Failed to load history: {error.message}</div>;

  return (
    <div>
      <h2 className="page-title">Build History</h2>

      {summaryStats && (
        <div className="stats-grid" style={{ marginBottom: 16 }}>
          <div className="stat-card" style={{ borderLeft: '4px solid #0984e3' }}>
            <div className="stat-value">{summaryStats.total}</div>
            <div className="stat-label">Total Builds</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #00b894' }}>
            <div className="stat-value">{summaryStats.successRate}%</div>
            <div className="stat-label">Success Rate</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #d63031' }}>
            <div className="stat-value">{summaryStats.failed}</div>
            <div className="stat-label">Failed</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #6c5ce7' }}>
            <div className="stat-value">{summaryStats.avgDuration}</div>
            <div className="stat-label">Avg Duration</div>
          </div>
        </div>
      )}

      <div className="card">
        {builds.length === 0 ? (
          <p>No builds found. <Link to="/build" className="link">Trigger a build</Link> to get started.</p>
        ) : (
          <>
            <DataTable columns={columns} data={builds} searchPlaceholder="Search builds..." />
            {nextToken && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <button className="btn btn-primary" onClick={() => fetchMore({ variables: { nextToken } })}>Load More</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
